import type { Trace, TraceEvent } from '@shared/trace';

/**
 * Browser-side API client.
 *
 * Cookies carry the session, so every call is credentialed. Errors are surfaced
 * as thrown `ApiError`s with the server's own message — during a live demo, a
 * generic "something went wrong" is the worst possible outcome, because the
 * presenter cannot tell whether the product misbehaved or the demo did.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await response.text();
  const payload = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(payload.message || payload.error || response.statusText, response.status, payload.error);
  }
  return payload as T;
}

export const api = {
  /** Exchange the shared password for a pass cookie on the hosted deployment. */
  signIn: (password: string) => request<{ ok: true }>('POST', '/access', { password }),

  bootstrap: () => request<BootstrapPayload>('GET', '/bank/bootstrap'),
  setOperator: (operatorId: string) => request<{ operatorId: string }>('POST', '/bank/operator', { operatorId }),

  operate: (input: OperateInput) => request<OperationOutcome>('POST', '/bank/operate', input),
  shell: (input: { hostId: string; command: string }) => request<OperationOutcome>('POST', '/ops/shell', input),
  machineAction: (input: { hostId: string; action: string }) =>
    request<{ ok: boolean; summary: string; note?: string }>('POST', '/ops/machine-action', input),

  hosts: () => request<HostsPayload>('GET', '/ops/hosts'),
  realInventory: () => request<RealInventoryPayload>('GET', '/ops/real-inventory'),
  pipelines: () => request<PipelinesPayload>('GET', '/ci/pipelines'),
  cloud: () => request<CloudPayload>('GET', '/ci/cloud'),
  runPipeline: (pipelineId: string) => request<OperationOutcome>('POST', `/ci/pipelines/${pipelineId}/run`),

  runScenario: (scenarioId: string) => request<ScenarioRunResult>('POST', `/scenarios/${scenarioId}/run`),

  traces: () => request<{ traces: TraceListItem[] }>('GET', '/traces'),
  trace: (traceId: string) => request<{ trace: Trace }>('GET', `/traces/${traceId}`),

  approvals: () => request<{ approvals: PendingApproval[] }>('GET', '/approvals'),
  resolveApproval: (traceId: string, decision: 'approved' | 'rejected', approverId: string) =>
    request<{ ok: boolean; resolvedBy: string }>('POST', `/approvals/${traceId}`, { decision, approverId }),
};

/**
 * Subscribes to the live trace stream.
 *
 * `EventSource` reconnects on its own, which is what keeps a demo alive across a
 * flaky conference network without the presenter having to reload the page and
 * lose their place.
 */
export function subscribeToTraces(onEvent: (event: TraceEvent) => void): () => void {
  const source = new EventSource('/api/stream', { withCredentials: true });
  const types: TraceEvent['type'][] = [
    'trace.started',
    'trace.step',
    'trace.line',
    'trace.mirror',
    'trace.completed',
    'approval.updated',
  ];

  const handler = (event: MessageEvent) => {
    try {
      onEvent(JSON.parse(event.data) as TraceEvent);
    } catch {
      /* a malformed frame should never take the console down mid-demo */
    }
  };

  for (const type of types) source.addEventListener(type, handler as EventListener);
  return () => {
    for (const type of types) source.removeEventListener(type, handler as EventListener);
    source.close();
  };
}

// ------------------------------------------------------------------- payloads

export interface OperateInput {
  toolName: string;
  args: Record<string, unknown>;
  hostId?: string;
  scenarioId?: string;
  intentText?: string;
  injectionSource?: string;
}

export interface OperationOutcome {
  traceId: string;
  outcome: string;
  decision: string;
  reason: string;
  readsCredentialFile?: string;
}

export interface TraceListItem {
  id: string;
  title: string;
  domain: string;
  outcome?: string;
  startedAt: number;
  endedAt?: number;
  scenarioId?: string;
  narration?: Trace['narration'];
}

export interface PendingApproval {
  traceId: string;
  actionId?: string;
  approvers: string[];
  operationTitle: string;
  createdAt: number;
}

export interface ScenarioSummary {
  id: string;
  title: string;
  domain: string;
  proves: string;
  pitch: string;
  expectedOutcome: string;
  surfaces: string[];
  talkTrack: string[];
}

export interface ScenarioRunResult {
  scenarioId: string;
  title: string;
  proves: string;
  surfaces: string[];
  talkTrack: string[];
  results: Array<OperationOutcome & { note: string }>;
}

export interface BootstrapPayload {
  bank: { name: string; tagline: string };
  session: { id: string; operatorId: string; operationsUsed: number; operationCap: number };
  controlPlane: { mode: 'live' | 'offline'; dashboardUrl: string; apiUrl: string };
  estate: {
    domains: any[];
    hosts: any[];
    mcpServers: any[];
    tools: any[];
    agents: any[];
    staff: any[];
    pipelines: any[];
    cloudAccounts: any[];
    policies: any[];
    summary: Record<string, number>;
  };
  records: {
    customers: any[];
    accounts: any[];
    transactions: any[];
    cards: any[];
    tickets: any[];
    kycCases: any[];
    loans: any[];
  };
  scenarios: ScenarioSummary[];
}

export interface HostsPayload {
  hosts: any[];
  fleet: {
    mode: string;
    bootstrapped: boolean;
    enrolled: Array<{ hostId: string; hostname: string; machineId: string; lastHeartbeatAt?: number }>;
    expected: number;
    lastError?: string;
  };
}

/**
 * Inventory the product genuinely discovered, kept in its own payload so it can
 * never be confused with the simulated estate in `HostsPayload`.
 */
export interface RealInventoryPayload {
  status: 'live' | 'pending' | 'unavailable' | 'off';
  reason?: string;
  fetchedAt?: string;
  machines: Array<{
    machineId: string;
    hostname?: string;
    platform?: string;
    developerName?: string;
    lastHeartbeatAt?: string;
  }>;
  agents: Array<{
    id: string;
    name: string;
    source: string;
    environment?: string;
    owner?: string;
    toolCount: number;
    lastSeenAt?: string;
  }>;
  tools: Array<{
    id: string;
    name: string;
    type: string;
    source: string;
    riskLevel?: string;
    riskTags: string[];
    serverName?: string;
    lastSeenAt?: string;
  }>;
  excludedSimulated: { machines: number; agents: number; tools: number };
}

export interface PipelinesPayload {
  pipelines: any[];
  coverage: { total: number; gated: number; gaps: string[] };
}

export interface CloudPayload {
  accounts: any[];
  summary: { workloads: number; unsanctioned: number; internetFacing: number };
}
